/**
 * Data loading for the PUBLIC grammar pages (/:lang/grammar/**).
 *
 * The authenticated grammar experience joins two files, and the public pages
 * need the same join to look like it:
 *
 *   public/data/grammar-levels/{level}-grammar_en.json   the lesson content
 *                                                        (pillars: dialogue,
 *                                                        explanation, practice)
 *   public/data/grammar/grammar-{level}.json             the exercise bank
 *                                                        (2,769 exercises total)
 *
 * The two are keyed differently. Pillars carry a numeric `id`; exercises carry a
 * topic slug in `pillar` ("connectors"). `pillarIdToTopicMap` in
 * services/grammarTopicsMap.js is the bridge: 'B1-1' -> 'connectors'. This is the
 * same join GrammarHub.createPillarConnections does, reduced to the read-only
 * parts — no Firebase, no progress, no subscription lookup, so it runs for a
 * logged-out visitor and during prerendering.
 *
 * Exercise counts are read rather than hardcoded because they are published as
 * fact on a public page: B1 pillar 1 really does have 71 exercises.
 */
import { useEffect, useState } from 'react';
import { pillarIdToTopicMap } from '../../services/grammarTopicsMap';

/** 'b1' + pillar id 1 -> 'connectors' */
export function topicIdFor(level, pillarId) {
  return pillarIdToTopicMap[`${String(level).toUpperCase()}-${pillarId}`] || null;
}

const TYPE_LABEL = {
  multipleChoice: 'Multiple choice',
  fillInTheBlank: 'Fill in the blank',
  wordOrder: 'Word order',
  sentenceOrdering: 'Word order',
  transformSentence: 'Transform sentence',
  transformation: 'Transform sentence',
  errorCorrection: 'Find the error',
  matching: 'Matching',
  rewrite: 'Rewrite',
};

export function exerciseTypeLabel(exercise) {
  if (!exercise) return null;
  const t = exercise.exerciseType || exercise.type;
  return TYPE_LABEL[t] || 'Practice';
}

/**
 * Loads both files for a level and returns a map keyed by pillar id:
 *   { [pillarId]: { exerciseCount, sample } }
 * `sample` is the first exercise for that pillar, used for the card preview.
 */
export function useGrammarExercises(level) {
  const [byPillar, setByPillar] = useState(null);

  useEffect(() => {
    if (!level) return undefined;
    let cancelled = false;

    fetch(`/data/grammar/grammar-${String(level).toLowerCase()}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((json) => {
        if (cancelled) return;
        // topic slug -> pillar number, for this level only
        const topicToPillar = {};
        const prefix = `${String(level).toUpperCase()}-`;
        Object.entries(pillarIdToTopicMap).forEach(([key, topic]) => {
          if (!key.startsWith(prefix)) return;
          const num = parseInt(key.slice(prefix.length), 10);
          if (!Number.isNaN(num)) topicToPillar[topic] = num;
        });

        const out = {};
        for (const ex of json.exercises || []) {
          const raw = ex.pillar ?? ex.topic;
          if (raw == null) continue;
          const asNum = Number(raw);
          const pillarId = Number.isNaN(asNum) ? topicToPillar[raw] : asNum;
          if (pillarId == null) continue;
          if (!out[pillarId]) out[pillarId] = { exerciseCount: 0, sample: ex };
          out[pillarId].exerciseCount += 1;
        }
        setByPillar(out);
      })
      // A missing exercise bank is not fatal: the cards simply render without
      // a count or a sample rather than failing the page.
      .catch(() => !cancelled && setByPillar({}));

    return () => {
      cancelled = true;
    };
  }, [level]);

  return byPillar;
}

/** Loads the pillar content for a level. */
export function useGrammarPillars(level) {
  const [pillars, setPillars] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!level) return undefined;
    let cancelled = false;
    fetch(`/data/grammar-levels/${String(level).toLowerCase()}-grammar_en.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((json) => !cancelled && setPillars(json.pillars || []))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [level]);

  return { pillars, failed };
}
